import React from "react";
import { useConnectAgent, useInputModeToggleAgent } from "rapida-react";
import { useEnsureVoiceAgent } from "rapida-react";
import { Send } from "lucide-react";
import { FC, HTMLAttributes, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AnimatePresence, motion } from "framer-motion";
// Removed Tailwind import: import '../ComponentStyles.css';
interface SimpleMessagingAcitonProps extends HTMLAttributes<HTMLDivElement> {
  placeholder?: string;
}
export const SimpleMessagingAction: FC<SimpleMessagingAcitonProps> = ({
  className,
  placeholder,
}) => {
  const ctx = useEnsureVoiceAgent();
  const { handleVoiceToggle } = useInputModeToggleAgent();
  const { handleConnectAgent, handleDisconnectAgent, isConnected } =
    useConnectAgent();
  const [isLoading, setIsLoading] = useState(false);
  //
  useEffect(() => {
    if (!isConnected) {
      setIsLoading(false);
    }
  }, [isConnected]);
  const handleDisconnectClick = (ctx: any) => {
    if (isConnected) {
      setIsLoading(true);
      handleDisconnectAgent(ctx);
    } else {
      handleConnectAgent(ctx);
    }
  };
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
  });
  const onSubmitForm = (data: any) => {
    ctx?.onSendText(data.message);
    reset();
  };
  // Inline styles definitions
  const styles = {
    container: {},
    endSessionContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "0.5rem 0"
    },
    endSessionContainerHidden: {
      display: "none"
    },
    endSessionButton: {
      padding: "0 0.75rem",
      paddingTop: "4px",
      paddingBottom: "4px",
      borderRadius: "9999px",
      display: "flex",
      alignItems: "center",
      backgroundColor: "#dc2626", // red-600
      color: "#ffffff",
      border: "1px solid rgba(185, 28, 28, 0.5)" // border-red-700/50
    },
    buttonIcon: {
      width: "1rem",
      height: "1rem",
      marginRight: "0.375rem"
    },
    buttonText: {
      fontSize: "0.875rem",
      fontWeight: 500
    },
    form: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      padding: "0.5rem",
      border: "1px solid #e5e7eb", // light mode border
      gap: "1rem",
      borderRadius: "9999px",
      transition: "border-color 0.2s ease"
    },
    formFocused: {
      borderColor: "#e95f43" // blue-600
    },
    formDarkMode: {
      borderColor: "#374151" // dark:border-gray-700
    },
    textarea: {
      resize: "none",
      padding: "0.5rem",
      height: "40px",
      width: "100%",
      fontSize: "1rem",
      border: "none",
      backgroundColor: "transparent",
      outline: "none",
      color: "white"
    },
    textareaDarkMode: {
      color: "#d1d5db", // dark:text-gray-300
    },
    textareaDisabled: {
      opacity: 0.5,
      pointerEvents: "none"
    },
    textareaPlaceholderDark: {
      color: "#6b7280" // dark:placeholder-gray-500
    },
    buttonsContainer: {
      position: "absolute",
      borderBottomLeftRadius: "0.5rem",
      borderBottomRightRadius: "0.5rem",
      right: "0.5rem",
      margin: "auto 0",
      width: "fit-content"
    },
    sendButton: {
      display: "inline-flex",
      flexShrink: 0,
      justifyContent: "center",
      alignItems: "center",
      height: "2.5rem",
      width: "2.5rem",
      borderRadius: "9999px",
      color: "#ffffff",
      backgroundColor: "#e95f43", // bg-blue-600
      transition: "background-color 0.2s ease"
    },
    sendButtonHover: {
      backgroundColor: "#rgb(158 67 49)" // darker blue on hover
    },
    sendIcon: {
      flexShrink: 0,
      width: "1.25rem",
      height: "1.25rem"
    },
    voiceButton: {
      position: "relative",
      display: "flex",
      width: "8rem",
      height: "3.25rem", // h-9
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "9999px",
      backgroundColor: "#e95f43", // bg-blue-600
      color: "#ffffff",
      transition: "opacity 0.2s ease",
      // minWidth: "2rem",
      padding: "0.5rem"
    },
    voiceButtonHover: {
      opacity: 0.7
    },
    voiceButtonDisabled: {
      color: "#f9fafb", // text-gray-50
      opacity: 0.3
    },
    voiceButtonIcon: {
      width: "1.25rem",
      height: "1.25rem"
    },
    voiceButtonText: {
      whiteSpace: "nowrap",
      paddingLeft: "0.25rem",
      paddingRight: "0.25rem",
      fontSize: "13px",
      fontWeight: 600
    },
    // New tooltip styles
    tooltipContainer: {
      position: "absolute",
      bottom: "100%",
      right: "0%",
      transform: "translateX(-50%)",
      marginBottom: "8px",
      zIndex: 10
    },
    tooltip: {
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      color: "#ffffff",
      padding: "8px 12px",
      borderRadius: "6px",
      fontSize: "12px",
      whiteSpace: "nowrap",
      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)"
    },
    tooltipArrow: {
      position: "absolute",
      top: "100%",
      left: "50%",
      transform: "translateX(-50%)",
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      borderTop: "6px solid rgba(0, 0, 0, 0.8)"
    }
  } as const;
  // State for hover effects
  const [isSendButtonHovered, setIsSendButtonHovered] = useState(false);
  const [isVoiceButtonHovered, setIsVoiceButtonHovered] = useState(false);
  const [isFormFocused, setIsFormFocused] = useState(false);
  
  // Voice tooltip animation variants
  const tooltipVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };
  
  return (
    <div style={styles.container}>
      <AnimatePresence>
        <motion.div
          style={!isConnected ? styles.endSessionContainerHidden : styles.endSessionContainer}
        >
          <button
            onClick={() => {
              handleDisconnectClick(ctx);
            }}
            disabled={isLoading}
            style={styles.endSessionButton}
          >
            {isLoading ? (
              <span>loading</span>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                style={styles.buttonIcon}
              >
                <path
                  fillRule="evenodd"
                  d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm6-2.438c0-.724.588-1.312 1.313-1.312h4.874c.725 0 1.313.588 1.313 1.313v4.874c0 .725-.588 1.313-1.313 1.313H9.564a1.312 1.312 0 0 1-1.313-1.313V9.564Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <span style={styles.buttonText}>End session</span>
          </button>
        </motion.div>
      </AnimatePresence>
      <form
        style={{
          ...styles.form,
          ...(isFormFocused ? styles.formFocused : {})
        }}
        className={className}
        onSubmit={handleSubmit(onSubmitForm)}
        onFocus={() => setIsFormFocused(true)}
        onBlur={() => setIsFormFocused(false)}
      >
        <textarea
          style={styles.textarea}
          placeholder={placeholder}
          {...register("message", {
            required: "Please write your message.",
          })}
          required
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
              handleSubmit(onSubmitForm)(e);
            }
          }}
        ></textarea>
        <div style={styles.buttonsContainer}>
          {isValid ? (
            <button
              type="submit"
              style={{
                ...styles.sendButton,
                ...(isSendButtonHovered ? styles.sendButtonHover : {})
              }}
              onMouseEnter={() => setIsSendButtonHovered(true)}
              onMouseLeave={() => setIsSendButtonHovered(false)}
            >
              <Send 
                style={styles.sendIcon} 
                strokeWidth="2" 
              />
            </button>
          ) : (
            <div style={{ position: "relative" }}>
              {/* Voice Button Tooltip */}
              <AnimatePresence>
                {isVoiceButtonHovered && (
                  <motion.div
                    style={styles.tooltipContainer}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={tooltipVariants}
                    transition={{ duration: 0.2 }}
                  >
                    <div style={styles.tooltip}>
                      Voice requires access to your Microphone
                      <div style={styles.tooltipArrow}></div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <button
                onClick={async () => {
                  !isConnected && (await handleConnectAgent(ctx));
                  await handleVoiceToggle(ctx);
                }}
                style={{
                  ...styles.voiceButton,
                  ...(isVoiceButtonHovered ? styles.voiceButtonHover : {})
                }}
                onMouseEnter={() => setIsVoiceButtonHovered(true)}
                onMouseLeave={() => setIsVoiceButtonHovered(false)}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg
                    viewBox="0 0 24 24"
                    style={styles.voiceButtonIcon}
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.5 4C8.67157 4 8 4.67157 8 5.5V18.5C8 19.3284 8.67157 20 9.5 20C10.3284 20 11 19.3284 11 18.5V5.5C11 4.67157 10.3284 4 9.5 4Z"
                      fill="currentColor"
                    ></path>
                    <path
                      d="M13 8.5C13 7.67157 13.6716 7 14.5 7C15.3284 7 16 7.67157 16 8.5V15.5C16 16.3284 15.3284 17 14.5 17C13.6716 17 13 16.3284 13 15.5V8.5Z"
                      fill="currentColor"
                    ></path>
                    <path
                      d="M4.5 9C3.67157 9 3 9.67157 3 10.5V13.5C3 14.3284 3.67157 15 4.5 15C5.32843 15 6 14.3284 6 13.5V10.5C6 9.67157 5.32843 9 4.5 9Z"
                      fill="currentColor"
                    ></path>
                    <path
                      d="M19.5 9C18.6716 9 18 9.67157 18 10.5V13.5C18 14.3284 18.6716 15 19.5 15C20.3284 15 21 14.3284 21 13.5V10.5C21 9.67157 20.3284 9 19.5 9Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </div>
                <span style={styles.voiceButtonText}>
                  Voice
                </span>
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};